<template>
  <teleport to="#modals">
    <vue-resizable
      v-slot="{ style }"
      class="z-[100]"
      drag-selector=".cursor-grab"
      v-bind="activeModalLocalStorageOptions"
      :min-width="activeModalWidthAndHeight.minWidth"
      :min-height="activeModalWidthAndHeight.minHeight"
      :max-width="activeModalWidthAndHeight?.maxWidth"
      :max-height="activeModalWidthAndHeight?.maxHeight"
      @mount="updatePosition"
      @resize:end="updatePosition"
      @drag:end="updatePosition"
    >
      <div
        ref="resizableElement"
        :style="style"
        class="rounded-md shadow-lg"
        :class="[
          isMinimized ? 'max-w-55' : 'h-full',
          isDealsView && isAllActivity ? 'bg-disabled' : 'bg-white',
          modalClass
        ]"
        data-cy="communication-dialog"
      >
        <div
          class="flex justify-between space-x-2 rounded-t-md bg-grey-navigation p-4 text-white"
          :class="{ 'max-w-55 rounded-b-md': isMinimized }"
          data-cy="communication-dialog-header"
        >
          <div
            class="flex min-w-0 flex-grow items-center space-x-4"
            data-cy="handle"
          >
            <icon-base
              :icon="IconCardView"
              width="16"
              height="16"
              view-box="0 0 24 24"
              class="handle min-w-4 rotate-90 transform cursor-grab"
            />
            <span
              class="grow cursor-grab overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-white"
              @dblclick="toggleMinimize"
            >
              {{ modalTitle }}
            </span>
          </div>
          <div class="flex space-x-2">
            <icon-base
              v-if="isMinimized"
              :icon="IconWidget"
              view-box="0 0 16 14"
              icon-name="Restore"
              :title="$t('COMMON.RESTORE')"
              class="cursor-pointer text-white"
              data-cy="maximize-communication-dialog"
              @click="toggleMinimize"
            />
            <icon-base
              v-else
              :icon="IconSubtract"
              view-box="0 -5 20 20"
              icon-name="Minimize"
              :title="$t('COMMON.MINIMIZE')"
              class="cursor-pointer text-white"
              data-cy="minimize-communication-dialog"
              @click="toggleMinimize"
            />
            <icon-base
              icon="x"
              icon-name="Close"
              data-cy="close-communication-dialog"
              class="cursor-pointer text-white"
              @click="emit('close-dialog')"
            />
          </div>
        </div>
        <loader :is-loading="loading" />
        <upload-call-recording
          v-if="
            uploadCallRecordingOptions.enabled &&
            uploadCallRecordingOptions.note
          "
          :call-log="uploadCallRecordingOptions.note"
          @call-note:uploaded="finishCallNoteUploaded"
        />
        <slot name="content">
          <!-- to prevent modal data from not being populated-->
          <component
            :is="modalTypeMap[modalType].component"
            v-if="!loading && !uploadCallRecordingOptions.enabled"
            v-show="!isMinimized"
            :class="modalTypeMap[modalType].componentClass"
            is-modal
            @close-dialog="emit('close-dialog')"
            @call-note:create="handleCallNoteCreate"
          />
        </slot>
      </div>
    </vue-resizable>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import type { DynamicFieldOptions } from "@/models/options";
import { useStore } from "vuex";
import { useLocalStorageSetting } from "@/hooks/options";
import useEmailsStore from "@/stores/emails";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconWidget from "@/components/icons/IconWidget.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import EmailForm from "@/components/emailSending/EmailForm.vue";
import SmsForm from "@/components/communicationLogs/communication/sms/SmsForm.vue";
import TaskForm from "@/components/communicationLogs/forms/CreateTaskForm.vue";
import CreateCallNoteForm from "@/components/communicationLogs/forms/CreateCallNoteForm.vue";
import UploadCallRecording from "@/components/communicationLogs/forms/UploadCallRecording.vue";
import VueResizable from "vue-resizable";
import type { ResizableEventValues } from "@/models/common";
import isEmpty from "lodash/isEmpty";
import throttle from "lodash/throttle";
import {
  DEFAULT_EMAIL_MODAL_OPTIONS,
  DEFAULT_SMS_MODAL_OPTIONS,
  DEFAULT_TASK_MODAL_OPTIONS,
  DEFAULT_CALL_MODAL_OPTIONS,
  DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS
} from "@/helpers/constants/deals";
import { convertToArray } from "@/helpers/common";
import { usePromiseWrapper } from "@/hooks/common";
import { ensurePositionWithinViewport } from "@/helpers/elements";
import { useRoute } from "vue-router";
import type {
  CommunicationModalType,
  CallLog
} from "@/models/communicationLogs";
import { allActivityType } from "@/helpers/communication";
import { useI18n } from "vue-i18n";
import useSmsStore from "@/stores/sms";
import { CommunicationType } from "@/enums/communicationLogs";
import { ROUTE_DEALS } from "@/router/routes";

type CallRecordingOptions = {
  enabled: boolean;
  note: CallLog | null;
};

const TOPBAR_HEIGHT = 53;

const emit = defineEmits<{
  "close-dialog": [];
}>();

const {
  initialValues = { x: 0, y: 0 },
  dealId = "",
  anchorBottomLeft = false,
  dealName = "",
  type = CommunicationType.email,
  title = ""
} = defineProps<{
  initialValues: { x: number; y: number };
  dealId: string | string[];
  anchorBottomLeft?: boolean;
  dealName?: string | undefined;
  type?: CommunicationModalType | typeof allActivityType;
  title?: string;
}>();

const { getters, dispatch } = useStore();
const emailStore = useEmailsStore();
const smsStore = useSmsStore();
const route = useRoute();
const { t } = useI18n();

const emailOptions = useLocalStorageSetting(
  "emailModalOptions",
  DEFAULT_EMAIL_MODAL_OPTIONS
);

const smsOptions = useLocalStorageSetting(
  "smsModalOptions",
  DEFAULT_SMS_MODAL_OPTIONS
);

const taskOptions = useLocalStorageSetting(
  "taskModalOptions",
  DEFAULT_TASK_MODAL_OPTIONS
);

const callOptions = useLocalStorageSetting(
  "callModalOptions",
  DEFAULT_CALL_MODAL_OPTIONS
);

const allActivityOptions = useLocalStorageSetting(
  "allActivityModalOptions",
  DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS
);

const uploadCallRecordingOptions = ref<CallRecordingOptions>({
  enabled: false,
  note: null
});

const emailWidthAndHeight = ref({
  width: DEFAULT_EMAIL_MODAL_OPTIONS.width,
  height: DEFAULT_EMAIL_MODAL_OPTIONS.height,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 400,
  maxHeight: undefined,
  minimizedWidth: 220,
  minimizedHeight: TOPBAR_HEIGHT
});

const smsWidthAndHeight = ref({
  width: DEFAULT_SMS_MODAL_OPTIONS.width,
  height: DEFAULT_SMS_MODAL_OPTIONS.height,
  minWidth: 550,
  maxWidth: undefined,
  minHeight: 400,
  maxHeight: undefined,
  minimizedWidth: 220,
  minimizedHeight: TOPBAR_HEIGHT
});

const taskWidthAndHeight = ref({
  width: DEFAULT_TASK_MODAL_OPTIONS.width,
  height: DEFAULT_TASK_MODAL_OPTIONS.height,
  minWidth: 260,
  maxWidth: 400,
  minHeight: 410,
  maxHeight: 410,
  minimizedWidth: 220,
  minimizedHeight: TOPBAR_HEIGHT
});

const callWidthAndHeight = ref({
  width: 500,
  height: 300,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 300,
  maxHeight: undefined,
  minimizedWidth: 220,
  minimizedHeight: TOPBAR_HEIGHT
});

const allActivityWidthAndHeight = ref({
  width: DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS.width,
  height: DEFAULT_ALL_ACTIVITY_MODAL_OPTIONS.height,
  minWidth: 500,
  maxWidth: undefined,
  minHeight: 300,
  maxHeight: undefined,
  minimizedWidth: 220,
  minimizedHeight: TOPBAR_HEIGHT
});

const modalTypeMap = {
  [CommunicationType.email]: {
    name: t("COMMON.EMAIL"),
    store: emailStore,
    options: emailOptions,
    widthAndHeight: emailWidthAndHeight,
    component: EmailForm,
    componentClass: undefined
  },
  [CommunicationType.sms]: {
    name: t("COMMON.SMS"),
    store: smsStore,
    options: smsOptions,
    widthAndHeight: smsWidthAndHeight,
    component: SmsForm,
    componentClass: undefined
  },
  [CommunicationType.task]: {
    name: t("COMMON.TASK"),
    store: undefined,
    options: taskOptions,
    widthAndHeight: taskWidthAndHeight,
    component: TaskForm,
    componentClass: undefined
  },
  [CommunicationType.call]: {
    name: t("COMMON.CALL"),
    store: undefined,
    options: callOptions,
    widthAndHeight: callWidthAndHeight,
    component: CreateCallNoteForm,
    componentClass: `h-[calc(100%-${TOPBAR_HEIGHT}px)]`
  },
  [allActivityType]: {
    name: t("COMMON.ALL"),
    store: undefined,
    options: allActivityOptions,
    widthAndHeight: allActivityWidthAndHeight,
    component: undefined,
    componentClass: undefined
  }
};

const isMinimized = ref(false);
const resizableElement = ref<HTMLElement | null>(null);

const dynamicFieldsOptions = computed<DynamicFieldOptions | null>(
  () => getters["options/dynamicFields"]
);

const isEmailModal = computed(() => type === CommunicationType.email);
const modalType = computed(() => type);

const isDealsView = computed(() => route.name === ROUTE_DEALS);

const isAllActivity = computed(() => modalType.value === allActivityType);

const activeModalWidthAndHeight = computed(
  () => modalTypeMap[modalType.value].widthAndHeight.value
);

const activeModalLocalStorageOptions = computed(
  () => modalTypeMap[modalType.value].options.value
);

const modalTitle = computed(
  () =>
    title ||
    `${modalTypeMap[modalType.value].name}${dealName ? ` - ${dealName}` : ""}`
);

const modalClass = computed(() => (route.params.id ? "deal-modal" : ""));

const initPosition = async (position: { x: number; y: number }) => {
  const { width, height } = activeModalWidthAndHeight.value;
  const left = anchorBottomLeft ? position.x + width : position.x;
  const top = anchorBottomLeft ? position.y - height : position.y;
  await updatePosition(
    {
      left,
      top,
      width,
      height
    },
    true
  );
};

const updatePosition = async (
  positionAndEvent: ResizableEventValues,
  init = false
) => {
  modalTypeMap[modalType.value].options.value = {
    ...modalTypeMap[modalType.value].options.value,
    left: positionAndEvent.left - (init ? 150 : 0),
    top: positionAndEvent.top + (init ? 50 : 0),
    width: positionAndEvent.width,
    height: positionAndEvent.height
  };
  await repositionAsNeeded();
};

const repositionAsNeeded = async (force = true) => {
  const { left: currentLeft, top: currentTop } =
    activeModalLocalStorageOptions.value;
  const draggableBounding = resizableElement.value?.getBoundingClientRect();
  const updates = await ensurePositionWithinViewport(
    activeModalLocalStorageOptions.value,
    draggableBounding
  );
  if (!force && currentLeft === updates.x && currentTop === updates.y) {
    return;
  }
  modalTypeMap[modalType.value].options.value = {
    ...modalTypeMap[modalType.value].options.value,
    // Ensure every update triggers a check for a modal position
    left: currentLeft === updates.x ? updates.x - 1 : updates.x,
    top: currentTop === updates.y ? updates.y - 1 : updates.y
  };
};

const resizeListener = throttle(
  async () => {
    await repositionAsNeeded(false);
  },
  100,
  { leading: false }
);

const toggleMinimize = () => {
  if (!isMinimized.value) {
    const { width, height } = activeModalLocalStorageOptions.value;
    activeModalLocalStorageOptions.value.restoreHeight = height;
    activeModalLocalStorageOptions.value.restoreWidth = width;
  }
  isMinimized.value = !isMinimized.value;

  const { width, height, minimizedWidth, minimizedHeight } =
    activeModalWidthAndHeight.value;

  const { restoreWidth, restoreHeight, left, top } =
    activeModalLocalStorageOptions.value;

  const setWidth = isMinimized.value ? minimizedWidth : restoreWidth || width;
  const setHeight = isMinimized.value
    ? minimizedHeight
    : restoreHeight || height;

  updatePosition({
    left,
    top,
    width: setWidth,
    height: setHeight
  });
};

const { fetchWrapper: getCommunicationInfo, loading } = usePromiseWrapper(
  async () => {
    if (
      modalType.value !== allActivityType &&
      [CommunicationType.email, CommunicationType.sms].includes(modalType.value)
    ) {
      if (isEmpty(dynamicFieldsOptions.value)) {
        await dispatch("options/getDynamicFields");
      }
      const action = isEmailModal.value
        ? emailStore.getApplicationEmailModalData
        : smsStore.getApplicationSmsModalData;
      await action(convertToArray(dealId));
    }

    const { left, top } = modalTypeMap[modalType.value].options.value;
    if (Number.isNaN(left) || Number.isNaN(top)) {
      initPosition(initialValues);
    }
    await repositionAsNeeded();
  }
);

const handleCallNoteCreate = (upload: boolean, note: CallLog) => {
  if (upload) {
    uploadCallRecordingOptions.value.enabled = true;
    uploadCallRecordingOptions.value.note = note;
    return;
  }
  emit("close-dialog");
};

const finishCallNoteUploaded = () => {
  uploadCallRecordingOptions.value.enabled = false;
  uploadCallRecordingOptions.value.note = null;
  emit("close-dialog");
};

onMounted(() => {
  getCommunicationInfo();
  window.addEventListener("resize", resizeListener);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeListener);
});
</script>
